import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { ApiConstants } from '../base/api-constants';
import { HttpBaseService } from '../base/http-base.service';
import { ReferenceTreeViewComponent } from '../components/reference-tree-view/reference-tree-view.component';
import { UtilService } from './util.service';

export interface WhereUsedConfig {
  /** The unique identifier of the item */
  id: string;
  /** The display name of the item */
  name: string;
  /** The type of the item (e.g., 'Foreign Key', 'Index', 'Lookup Definition') */
  type: string;
  /** Optional: API name to use for fetching references (defaults to 'getReferences') */
  apiName?: string;
  /** Optional: Parameter name for the ID (defaults to 'modelId', can be 'appId' for lifecycle) */
  idParamName?: 'modelId' | 'appId';
}

export interface ReferenceTreeConfig {
  /** Node information for the reference tree */
  node: {
    name: string;
    type: string;
  };
  /** The list of references */
  referenceList: any[];
  /** The action being performed (e.g., 'WHERE_USED', 'DELETE', 'UPDATE') */
  action: string;
  /** Optional: Additional data to pass to the dialog */
  rawData?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ListPageActionService {
  private destroyRef = inject(DestroyRef);
  private httpBaseService = inject(HttpBaseService);
  private translateService = inject(TranslateService);
  private utilService = inject(UtilService);

  /**
   * Fetches references for a given model ID.
   *
   * @param id - The model ID to fetch references for
   * @param apiName - The API constant key to use (defaults to 'getReferences')
   * @param idParamName - The parameter name for the ID (defaults to 'modelId')
   * @returns Promise that resolves with the references or rejects with an error
   */
  getReferences(
    id: string,
    apiName: string = 'getReferences',
    idParamName: 'modelId' | 'appId' = 'modelId'
  ): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.httpBaseService
        .get(ApiConstants[apiName], { [idParamName]: id })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => {
            resolve(res);
          },
          error: (err: any) => {
            reject(err);
          }
        });
    });
  }

  /**
   * Shows the "Where Used" functionality for an item.
   * Fetches references and either shows the reference tree or a "no references" dialog.
   *
   * @param config - Configuration for the where-used lookup
   * @param dialogService - The DialogService instance from the component
   * @param callbacks - Optional callbacks for loading state management
   * @returns Promise that resolves when the operation is complete
   */
  async showWhereUsed(
    config: WhereUsedConfig,
    dialogService: DialogService,
    callbacks?: {
      hideLoader?: () => void;
    }
  ): Promise<void> {
    const apiName = config.apiName || 'getReferences';
    const idParamName = config.idParamName || 'modelId';

    try {
      const references = await this.getReferences(config.id, apiName, idParamName);

      callbacks?.hideLoader?.();

      if (references && references.length > 0) {
        this.openReferenceTreeDialog(dialogService, {
          node: { name: config.name, type: config.type },
          referenceList: references,
          action: 'WHERE_USED'
        });
      } else {
        this.utilService.showNoReferencesDialog(config.name, config.type);
      }
    } catch (error) {
      callbacks?.hideLoader?.();
      console.error('Error fetching references:', error);
      this.utilService.showError(this.translateService.instant('ERROR_FETCHING_REFERENCES'));
    }
  }

  /**
   * Opens the reference tree dialog.
   *
   * @param dialogService - The DialogService instance from the component
   * @param config - Configuration for the reference tree dialog
   * @param onClose - Optional callback when the dialog closes
   * @returns The dialog reference
   */
  openReferenceTreeDialog(dialogService: DialogService, config: ReferenceTreeConfig) {
    const modalRef = dialogService.open(ReferenceTreeViewComponent, {
      showHeader: false,
      styleClass: 'reference-tree',
      data: {
        node: config.node,
        referenceList: config.referenceList,
        action: config.action
      }
    });

    return modalRef;
  }
}
